import React, { useState, useEffect, useMemo } from 'react';
import {
    Typography,
    Card,
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    message,
    Tabs,
    Tag,
    Alert,
    Upload,
    Select,
    Tooltip
} from 'antd';
import {
    PlusOutlined,
    UploadOutlined,
    UserAddOutlined,
    BookOutlined,
    FileExcelOutlined,
    FileOutlined,
    EditOutlined,
    PoweroffOutlined,
    CheckCircleOutlined,
    StopOutlined,
    DeleteOutlined,
    EyeOutlined,
    RobotOutlined,
    UserOutlined,
    BarChartOutlined,
    CopyOutlined,
    LineChartOutlined,
    SearchOutlined,
    FilterOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import {
    getMoodlePrograms,
} from '../services/moodleService';
import {
    adminAssignParticipants,
    adminBulkUploadQuestions,
    adminToggleTrainingStatus,
    adminUpdateTraining,
    adminClearSubmissionsByTraining,
    adminGetEnrollmentRequests,
    adminApproveEnrollment,
    adminRejectEnrollment,
    adminGetTrainingById
} from '../services/adminService';
import { TrainingMaster, TrainingType, TrainingStatus } from '../types/training';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

const AdminDashboard: React.FC = () => {
    const [trainings, setTrainings] = useState<TrainingMaster[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [filterMode, setFilterMode] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
    const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);

    const [selectedTraining, setSelectedTraining] = useState<TrainingMaster | null>(null);
    const [enrollmentRequests, setEnrollmentRequests] = useState<any[]>([]);

    const [assignForm] = Form.useForm();
    const [uploadForm] = Form.useForm();
    const navigate = useNavigate();

    useEffect(() => {
        fetchTrainings();
        fetchEnrollmentRequests();
    }, []);

    const fetchTrainings = async () => {
        try {
            setLoading(true);
            const data = await getMoodlePrograms();
            setTrainings(data);
        } catch (error) {
            message.error('Failed to fetch trainings');
        } finally {
            setLoading(false);
        }
    };

    const fetchEnrollmentRequests = async () => {
        try {
            const data = await adminGetEnrollmentRequests();
            setEnrollmentRequests(data || []);
        } catch (error) {
            console.error('Failed to fetch enrollment requests', error);
        }
    };

    const handleAssignParticipants = async (values: any) => {
        if (!selectedTraining) return;
        try {
            setLoading(true);
            const emails = values.Emails.split(/[;,]+/).map((e: any) => e.trim()).filter(Boolean);
            const rawModules = (selectedTraining.assessments || '').split('|').map((m: any) => m.trim()).filter(Boolean);
            const modules = rawModules.map(m => m.replace(/\[.*?\]$/, '').trim());

            if (modules.length === 0) {
                message.error('No modules defined for this training. Please edit the program to add modules.');
                return;
            }

            const role = values.Role || 'student';
            await adminAssignParticipants(selectedTraining.trainingId, modules, emails, role);

            // Sync emails in the master record based on role
            const updatePayload: any = { ...selectedTraining };
            
            if (role === 'editingteacher') {
                const currentTrainers = (selectedTraining.trainerEmails || '').split(/[;,]+/).map(e => e.trim()).filter(Boolean);
                const updatedTrainers = Array.from(new Set([...currentTrainers, ...emails])).join(';');
                updatePayload.trainerEmails = updatedTrainers;
            } else {
                const currentAudience = (selectedTraining.audienceEmails || '').split(/[;,]+/).map(e => e.trim()).filter(Boolean);
                const updatedAudience = Array.from(new Set([...currentAudience, ...emails])).join(';');
                updatePayload.audienceEmails = updatedAudience;
            }

            const currentVersion = parseFloat(selectedTraining.notificationVersion || '0.0') || 0;
            updatePayload.notificationVersion = (currentVersion + 1.0).toFixed(1);

            await adminUpdateTraining(selectedTraining.id, updatePayload);

            message.success(`${values.Role === 'editingteacher' ? 'Trainers' : 'Participants'} assigned and record synced.`);
            setIsAssignModalVisible(false);
            assignForm.resetFields();
            fetchTrainings();
        } catch (error) {
            message.error('Failed to assign participants');
        } finally {
            setLoading(false);
        }
    };

    const handleBulkUpload = async (values: any) => {
        if (!selectedTraining) {
            message.error('No training selected');
            return;
        }
        try {
            setLoading(true);
            let questionData: any[] = [];

            const file = values.ExcelFile[0].originFileObj;
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            questionData = jsonData
                .map((row: any) => ({
                    Question: String(row.Question || row.question || '').trim(),
                    CodeSnippet: String(row['Code Snippet'] || row.CodeSnippet || row['Code snippet'] || '').trim(),
                    OptionA: String(row.OptionA || row.optionA || '').trim(),
                    OptionB: String(row.OptionB || row.optionB || '').trim(),
                    OptionC: String(row.OptionC || row.optionC || '').trim(),
                    OptionD: String(row.OptionD || row.optionD || '').trim(),
                    Answer: String(row.Answer || row.answer || '').trim().toUpperCase()
                }))
                .filter(q => q.Question.length > 0 && q.Answer.length > 0);

            if (questionData.length === 0) {
                throw new Error('No valid questions found. Ensure "Question" and "Answer" columns are filled.');
            }

            await adminBulkUploadQuestions(selectedTraining.trainingId, values.ModuleID, questionData);
            message.success(`${questionData.length} questions uploaded successfully`);
            setIsUploadModalVisible(false);
            uploadForm.resetFields();
        } catch (error: any) {
            message.error(`Upload failed: ${error.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = (record: TrainingMaster) => {
        const action = record.active ? 'deactivate' : 'activate';
        Modal.confirm({
            title: `${action.charAt(0).toUpperCase() + action.slice(1)} Program?`,
            content: `Are you sure you want to ${action} "${record.trainingName}"?`,
            okText: 'Yes',
            okType: record.active ? 'danger' : 'primary',
            cancelText: 'No',
            onOk: async () => {
                try {
                    setLoading(true);
                    await adminToggleTrainingStatus(record.id, record.active);
                    if (record.active) {
                        await adminClearSubmissionsByTraining(record.trainingId);
                    }
                    message.success(`Program ${action}d successfully`);
                    fetchTrainings();
                } catch (error) {
                    message.error(`Failed to ${action} program`);
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleApproveEnrollment = async (id: number) => {
        try {
            setLoading(true);
            await adminApproveEnrollment(id);
            message.success('Enrollment approved');
            fetchEnrollmentRequests();
        } catch (error) {
            message.error('Failed to approve enrollment');
        } finally {
            setLoading(false);
        }
    };

    const handleRejectEnrollment = async (id: number) => {
        Modal.confirm({
            title: 'Reject Request?',
            content: 'Are you sure you want to reject this enrollment request? This will delete the request.',
            okText: 'Yes, Reject',
            okType: 'danger',
            onOk: async () => {
                try {
                    setLoading(true);
                    await adminRejectEnrollment(id);
                    message.success('Enrollment request rejected');
                    fetchEnrollmentRequests();
                } catch (error) {
                    message.error('Failed to reject enrollment');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const filteredTrainings = useMemo(() => {
        return trainings.filter(t => {
            if (searchText) {
                const q = searchText.toLowerCase();
                if (!t.trainingName?.toLowerCase().includes(q) && !t.trainingId?.toLowerCase().includes(q))
                    return false;
            }
            if (filterMode === 'TrainerLed' && t.programType !== 'TrainerLed') return false;
            if (filterMode === 'AI' && t.programType === 'TrainerLed') return false;
            if (filterStatus === 'active' && !t.active) return false;
            if (filterStatus === 'draft' && !(t.active === false && t.ispublished === false)) return false;
            if (filterStatus === 'inactive' && !(t.active === false && t.ispublished !== false)) return false;
            return true;
        }).sort((a, b) => {
            const rank = (t: any) => t.active ? 0 : t.ispublished === false ? 1 : 2;
            const rankDiff = rank(a) - rank(b);
            if (rankDiff !== 0) return rankDiff;
            // Within same group: nearest end date first; evergreen (no date) last
            const endA = a.endDate ? dayjs(a.endDate).valueOf() : Infinity;
            const endB = b.endDate ? dayjs(b.endDate).valueOf() : Infinity;
            return endA - endB;
        });
    }, [trainings, searchText, filterMode, filterStatus]);

    const isFiltered = !!(searchText || filterMode || filterStatus);

    const columns = [
        {
            title: 'Training ID',
            dataIndex: 'trainingId',
            key: 'trainingId',
            render: (id: string) => (
                <Tooltip title={id}>
                    <div style={{ 
                        maxWidth: '120px', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        fontFamily: 'monospace',
                        fontSize: '12px'
                    }}>
                        {id}
                    </div>
                </Tooltip>
            )
        },
        {
            title: 'Name',
            dataIndex: 'trainingName',
            key: 'trainingName',
            render: (name: string, record: any) => (
                <Typography.Link onClick={() => navigate(`/admin/edit/${record.id}`)}>
                    {name}
                </Typography.Link>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'trainingType',
            key: 'trainingType',
            render: (type: TrainingType) => {
                let color = 'blue';
                if (type === TrainingType.OPEN) color = 'green';
                if (type === TrainingType.REQUESTABLE) color = 'orange';
                return <Tag color={color}>{type || TrainingType.PRIVATE}</Tag>;
            }
        },
        {
            title: 'Mode',
            key: 'programType',
            render: (_: any, record: TrainingMaster) => {
                const isTrainerLed = record.programType === 'TrainerLed';
                return (
                    <Tag 
                        icon={isTrainerLed ? <UserOutlined /> : <RobotOutlined />} 
                        color={isTrainerLed ? 'purple' : 'cyan'}
                        style={{ borderRadius: '4px', fontWeight: 500 }}
                    >
                        {isTrainerLed ? 'Trainer Led' : 'AI Led'}
                    </Tag>
                );
            }
        },
        {
            title: 'Status',
            key: 'active',
            render: (_: any, record: TrainingMaster) => {
                if (record.active) return <Tag color="success">ACTIVE</Tag>;
                if (record.ispublished === false) return <Tag color="orange">DRAFT</Tag>;
                return <Tag color="error">INACTIVE</Tag>;
            },
        },
        {
            title: 'Created By',
            key: 'createdBy',
            width: 200,
            render: (_: any, record: TrainingMaster) => (
                record.createdBy
                    ? <Tooltip title={record.createdBy}><span style={{ fontSize: '12px', color: '#595959' }}>{record.createdBy}</span></Tooltip>
                    : <span style={{ color: '#bfbfbf', fontSize: '12px' }}>—</span>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: TrainingMaster) => (
                <Space size="middle">
                    <Button
                        icon={record.active ? <EditOutlined /> : <EyeOutlined />}
                        onClick={() => navigate(`/admin/edit/${record.id}`)}
                        style={{ width: '110px', background: 'transparent', borderColor: '#8c8c8c' }}
                    >
                        {record.active ? 'Edit' : 'View Details'}
                    </Button>
                    {/* Assign button hidden — planned for next phase */}
                    <Button
                        icon={<LineChartOutlined />}
                        onClick={() => {
                            if (record.programType === 'TrainerLed') {
                                navigate(`/trainer-led-training-details/${encodeURIComponent(record.trainingId)}?mode=analyze`);
                            } else {
                                navigate(`/training/${encodeURIComponent(record.trainingId)}?mode=analyze`);
                            }
                        }}
                        type="primary"
                        ghost
                        style={{ width: '110px', borderColor: '#fa541c', color: '#fa541c', background: 'transparent' }}
                    >
                        Analyze
                    </Button>
                    {(record.programType === 'AI' || record.programType === 'AINested') && (
                        <Button
                            icon={<CopyOutlined />}
                            onClick={() => navigate(`/admin/clone/${encodeURIComponent(record.trainingId)}`)}
                            style={{ width: '90px', background: 'transparent', borderColor: '#8c8c8c' }}
                        >
                            Clone
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    const requestColumns = [
        {
            title: 'Training ID',
            dataIndex: 'TrainingID',
            key: 'TrainingID',
        },
        {
            title: 'Participant Email',
            dataIndex: 'ParticipantEmail',
            key: 'ParticipantEmail',
        },
        {
            title: 'Request Date',
            dataIndex: 'AssignedDate',
            key: 'AssignedDate',
            render: (date: string) => date ? dayjs(date.split('T')[0]).format('DD/MM/YYYY') : '-'
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: any) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={() => handleApproveEnrollment(record.Id)}
                    >
                        Approve
                    </Button>
                    <Button
                        danger
                        icon={<StopOutlined />}
                        onClick={() => handleRejectEnrollment(record.Id)}
                    >
                        Reject
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: '24px', maxWidth: '95%', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{
                        width: 56, height: 56, borderRadius: '12px',
                        backgroundColor: '#fff7e6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        marginRight: 20
                    }}>
                        <BookOutlined style={{ fontSize: '28px', color: '#fa541c' }} />
                    </div>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>GrowthHub Admin</Title>
                        <Text type="secondary">Manage training programs, participants, and assessments</Text>
                    </div>
                </div>
                <Space>
                    <Button
                        icon={<BarChartOutlined />}
                        size="large"
                        onClick={() => navigate('/admin/executive-dashboard')}
                        style={{ height: '48px', borderRadius: '8px' }}
                    >
                        Executive Visibility
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="large"
                        onClick={() => navigate('/admin/create')}
                        style={{ backgroundColor: '#fa541c', borderColor: '#fa541c', height: '48px', padding: '0 32px', borderRadius: '8px', color: '#fff' }}
                    >
                        Create Program
                    </Button>
                </Space>
            </div>

            <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f0f0f0' }}>
                <Tabs defaultActiveKey="1">
                    <TabPane
                        tab={<span><BookOutlined style={{ color: '#fa541c', marginRight: 8 }} />Program Management</span>}
                        key="1"
                    >
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16, flexWrap: 'wrap' }}>
                            <Input
                                placeholder="Search by name or ID..."
                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                allowClear
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                                style={{ width: 280 }}
                            />
                            <Select
                                value={filterMode || undefined}
                                placeholder="All Modes"
                                allowClear
                                onClear={() => setFilterMode('')}
                                onChange={val => setFilterMode(val || '')}
                                style={{ width: 150 }}
                                options={[
                                    { value: 'TrainerLed', label: 'Trainer Led' },
                                    { value: 'AI', label: 'AI Led' },
                                ]}
                            />
                            <Select
                                value={filterStatus || undefined}
                                placeholder="All Statuses"
                                allowClear
                                onClear={() => setFilterStatus('')}
                                onChange={val => setFilterStatus(val || '')}
                                style={{ width: 150 }}
                                options={[
                                    { value: 'active', label: 'Active' },
                                    { value: 'draft', label: 'Draft' },
                                    { value: 'inactive', label: 'Inactive' },
                                ]}
                            />
                            {isFiltered && (
                                <Button
                                    size="small"
                                    icon={<FilterOutlined />}
                                    onClick={() => { setSearchText(''); setFilterMode(''); setFilterStatus(''); }}
                                >
                                    Clear filters
                                </Button>
                            )}
                            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 600, whiteSpace: 'nowrap' }}>Program Timeline:</span>
                                    {([
                                        { color: '#fef6e4', border: '#ffd591', label: 'Ending Soon' },
                                        { color: '#fde8e8', border: '#ffa39e', label: 'Overdue' },
                                    ] as { color: string; border: string; label: string }[]).map(({ color, border, label }) => (
                                        <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <span style={{ width: 12, height: 12, borderRadius: 3, background: color, border: `1px solid ${border}`, display: 'inline-block', flexShrink: 0 }} />
                                            <span style={{ fontSize: 11, color: '#8c8c8c', whiteSpace: 'nowrap' }}>{label}</span>
                                        </span>
                                    ))}
                                </div>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {isFiltered
                                        ? `${filteredTrainings.length} of ${trainings.length} programs`
                                        : `${trainings.length} programs`}
                                </Text>
                            </div>
                        </div>
                        <style>{`.admin-prog-table .ant-table-tbody > tr.ant-table-row:hover > td { background: inherit !important; }`}</style>
                        <Table
                            className="admin-prog-table"
                            columns={columns}
                            dataSource={filteredTrainings}
                            loading={loading}
                            rowKey="Id"
                            scroll={{ x: 'max-content' }}
                            style={{ marginTop: '16px' }}
                            onRow={(record: TrainingMaster) => {
                                if (!record.active || !record.endDate || !record.startDate) return {};
                                const today = dayjs().startOf('day');
                                const start = dayjs(record.startDate.toString().split('T')[0]);
                                const end = dayjs(record.endDate.toString().split('T')[0]);
                                const totalDays = end.diff(start, 'day');
                                if (totalDays <= 0) return {};
                                const remaining = end.diff(today, 'day');
                                // Program already ended but still marked active
                                if (remaining < 0) return { style: { background: '#fde8e8' } };
                                const fraction = remaining / totalDays;
                                // Only highlight ending soon (20–50% remaining)
                                if (fraction < 0.5) return { style: { background: '#fef6e4' } };
                                return {};
                            }}
                        />
                    </TabPane>
                    <TabPane
                        tab={<span><UserAddOutlined style={{ color: '#fa541c', marginRight: 8 }} />Enrollment Requests <Tag color="blue">{enrollmentRequests.length}</Tag></span>}
                        key="2"
                    >
                        <Table
                            columns={requestColumns}
                            dataSource={enrollmentRequests}
                            loading={loading}
                            rowKey="Id"
                            style={{ marginTop: '16px' }}
                        />
                    </TabPane>
                </Tabs>
            </Card>

            <Modal
                title={`Assign Participants: ${selectedTraining?.trainingName}`}
                open={isAssignModalVisible}
                onCancel={() => setIsAssignModalVisible(false)}
                onOk={() => assignForm.submit()}
                confirmLoading={loading}
            >
                <Form
                    form={assignForm}
                    layout="vertical"
                    onFinish={handleAssignParticipants}
                >
                    {selectedTraining?.programType === 'TrainerLed' && (
                        <Form.Item
                            label="Assign As"
                            name="Role"
                            initialValue="student"
                            rules={[{ required: true }]}
                        >
                            <Select>
                                <Select.Option value="student">Participant (Student)</Select.Option>
                                <Select.Option value="editingteacher">Trainer (Teacher)</Select.Option>
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item
                        label="Email Addresses (Semicolon separated)"
                        name="Emails"
                        rules={[{ required: true }]}
                    >
                        <TextArea rows={6} placeholder="user1@company.com; user2@company.com" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`Add Extra Questions: ${selectedTraining?.trainingName}`}
                open={isUploadModalVisible}
                onCancel={() => setIsUploadModalVisible(false)}
                onOk={() => uploadForm.submit()}
                confirmLoading={loading}
                width={800}
            >
                <Form
                    form={uploadForm}
                    layout="vertical"
                    onFinish={handleBulkUpload}
                >
                    <Form.Item label="Module ID (Submodule)" name="ModuleID" rules={[{ required: true }]}>
                        <Input placeholder="e.g. Introduction, Module 1" />
                    </Form.Item>

                    <Form.Item
                        name="ExcelFile"
                        label="Select Excel File"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                        rules={[{ required: true, message: 'Please upload an excel file' }]}
                    >
                        <Upload.Dragger beforeUpload={() => false} maxCount={1} accept=".xlsx,.xls">
                            <p className="ant-upload-drag-icon"><FileExcelOutlined /></p>
                            <p className="ant-upload-text">Click or drag file to this area to upload</p>
                        </Upload.Dragger>
                    </Form.Item>
                </Form>
            </Modal>

        </div>
    );
};

export default AdminDashboard;
